import { Controller } from "stimulus"

// Split out of pledge_flow_wizard_controller.js — talks to the wizard only through form field values and the payment-modal:paymentCompleted event, not step-navigation state.

export default class extends Controller {
  static targets = [
    "paymentOptions", "connectedPaymentMethod",
    "paymentMethodHiddenInput", "paymentMethodTypeInput", "paymentMethodCustomerIdInput",
  ]

  static values = {
    currencyToPaymentProcessorAccountIdMapping: Object,
  }

  initialize() {
    this.element.addEventListener('payment-modal:paymentCompleted', this.handlePaymentCompleted.bind(this))
  }

  // Donation Flow sets data-form-scope="donation_form"; defaults to pledge_form otherwise.
  formScope() {
    return this.element.dataset.formScope || 'pledge_form'
  }

  fieldSelector(name) {
    return `[name="${this.formScope()}[${name}]"]`
  }

  // What the donor is charged, for the wallet payment sheet
  currentAmountCents() {
    const cents = (name) => parseInt(this.element.querySelector(this.fieldSelector(name))?.value, 10) || 0
    return cents('amount_cents') + cents('tip_cents')
  }

  // Finds whichever wizard controller shares the wizard root.
  getWizardController() {
    const el = document.querySelector('[data-wizard-root]')
    if (!el) return null

    return (el.dataset.controller || '')
      .split(' ')
      .filter((identifier) => identifier && identifier !== 'payment-connection')
      .map((identifier) => this.application.getControllerForElementAndIdentifier(el, identifier))
      .find(Boolean) || null
  }

  onPaymentMethodButtonClicked(event) {
    event.preventDefault()

    const button = event.currentTarget
    const paymentMethodType = button.dataset.paymentMethodType
    const paymentMethodName = button.dataset.paymentMethodName
    const paymentMethodDescription = button.dataset.paymentMethodDescription

    // Donation Flow (e.g. HLI) has no partner_id field at all -- optional chaining here, not
    // `.value` directly, or this throws before clientId is ever read.
    const partnerId = this.element.querySelector(this.fieldSelector('partner_id'))?.value
    const clientId = this.element.querySelector(this.fieldSelector('client_id'))?.value
    const stripeAccountId = this.connectedStripeAccountId
    const partnerCurrency = this.getCurrentPartnerCurrency()

    const modalElement = document.querySelector('[data-controller*="payment-modal"]')
    if (modalElement) {
      const modalController = this.application.getControllerForElementAndIdentifier(modalElement, 'payment-modal')
      if (modalController) {
        modalController.show(paymentMethodType, partnerId, paymentMethodName, paymentMethodDescription, stripeAccountId, partnerCurrency, clientId)
      }
    }
  }

  handlePaymentCompleted(event) {
    const { buttonType, paymentMethodId, stripePaymentMethodType } = event.detail

    this.setPaymentMethodData(buttonType, paymentMethodId, stripePaymentMethodType)
    this.markPaymentMethodAsSelected(buttonType)
    this.showConnectedPaymentMethodState()
  }

  markPaymentMethodAsSelected(paymentMethodType) {
    this.clearSelectedPaymentButtons()

    const selectedButton = this.element.querySelector(`[data-payment-method-type="${paymentMethodType}"]`)
    if (selectedButton) selectedButton.classList.add('is-selected')
  }

  hasConnectedPaymentMethod() {
    const paymentMethodId = document.querySelector(this.fieldSelector('payment_processor_payment_method_id'))?.value
    const paymentMethodType = this.paymentMethodTypeInputTarget?.value

    return !!(paymentMethodId && paymentMethodType)
  }

  showConnectedPaymentMethodState() {
    const paymentOptions = this.element.querySelector('[data-payment-connection-target="paymentOptions"]')
    if (paymentOptions) paymentOptions.style.display = 'none'

    let connectedDisplay = this.element.querySelector('[data-payment-connection-target="connectedPaymentMethod"]')
    if (!connectedDisplay) {
      connectedDisplay = this.createConnectedPaymentMethodDisplay()
      paymentOptions.parentNode.insertBefore(connectedDisplay, paymentOptions)
    } else {
      this.updateConnectedPaymentMethodDisplay(connectedDisplay)
    }
    connectedDisplay.style.display = 'block'
  }

  createConnectedPaymentMethodDisplay() {
    const paymentMethodType = this.paymentMethodTypeInputTarget?.value
    const selectedButton = this.element.querySelector(`[data-payment-method-type="${paymentMethodType}"]`)
    const paymentMethodName = selectedButton?.dataset.paymentMethodName || 'Payment Method'

    const display = document.createElement('div')
    display.setAttribute('data-payment-connection-target', 'connectedPaymentMethod')
    display.className = 'connected-payment-method-display'
    display.innerHTML = `
      <div class="card portfolio-card">
        <div class="card-content">
          <div class="media">
            <div class="media-left">
              <figure class="image">
                <div class="payment-method-icon">
                  <i class="fas fa-check-circle" style="color: #2675F8; font-size: 1.5rem;"></i>
                </div>
              </figure>
            </div>
            <div class="media-content">
              <p class="title is-5">${paymentMethodName}</p>
              <p class="subtitle small">Payment method connected successfully</p>
            </div>
            <div class="media-right">
              <button type="button" class="button is-small is-outlined" data-action="click->payment-connection#replacePaymentMethod">
                Replace
              </button>
            </div>
          </div>
        </div>
      </div>
    `

    return display
  }

  updateConnectedPaymentMethodDisplay(connectedDisplay) {
    const paymentMethodType = this.paymentMethodTypeInputTarget?.value
    const selectedButton = this.element.querySelector(`[data-payment-method-type="${paymentMethodType}"]`)
    const paymentMethodName = selectedButton?.dataset.paymentMethodName || 'Payment Method'

    const titleElement = connectedDisplay.querySelector('.title.is-5')
    if (titleElement) titleElement.textContent = paymentMethodName
  }

  replacePaymentMethod(event) {
    event.preventDefault()

    const connectedDisplay = this.element.querySelector('[data-payment-connection-target="connectedPaymentMethod"]')
    if (connectedDisplay) connectedDisplay.style.display = 'none'

    const paymentOptions = this.element.querySelector('[data-payment-connection-target="paymentOptions"]')
    if (paymentOptions) paymentOptions.style.display = 'block'

    this.clearPaymentMethodData()
  }

  clearPaymentMethodData() {
    if (this.hasPaymentMethodHiddenInputTarget) this.paymentMethodHiddenInputTarget.value = ''
    if (this.hasPaymentMethodTypeInputTarget) this.paymentMethodTypeInputTarget.value = ''

    const paymentMethodIdInput = document.querySelector(this.fieldSelector('payment_processor_payment_method_id'))
    if (paymentMethodIdInput) paymentMethodIdInput.value = ''

    if (this.hasPaymentMethodCustomerIdInputTarget) this.paymentMethodCustomerIdInputTarget.value = ''

    this.clearSelectedPaymentButtons()
  }

  // Called by the wizard's resetFieldsOnCurrencyChange — a currency switch clears the picked
  // payment method, unless one is already connected (switching currency after connecting
  // shouldn't silently disconnect it).
  resetIfNotConnected() {
    if (this.hasConnectedPaymentMethod()) return

    if (this.hasPaymentMethodHiddenInputTarget) this.paymentMethodHiddenInputTarget.value = null
    if (this.hasPaymentMethodTypeInputTarget) this.paymentMethodTypeInputTarget.value = null
    if (this.hasPaymentMethodCustomerIdInputTarget) this.paymentMethodCustomerIdInputTarget.value = null

    this.clearSelectedPaymentButtons()
  }

  clearSelectedPaymentButtons() {
    this.element.querySelectorAll('.payment-method-button').forEach((button) => {
      button.classList.remove('is-selected')
    })
  }

  getCurrentPartnerCurrency() {
    // Donation Flow (e.g. HLI): currency is a direct field on the form, no Partner to derive it from.
    if (this.formScope() === 'donation_form') {
      return this.element.querySelector(this.fieldSelector('currency_code'))?.value || null
    }

    const partnerId = this.element.querySelector(this.fieldSelector('partner_id'))?.value
    if (!partnerId) return null

    const partnerElement = this.element.querySelector(`[data-partner-id="${partnerId}"]`)
    if (partnerElement) {
      const currency = partnerElement.dataset.currency
      if (currency) return currency
    }

    const currencyMapping = this.currencyToPaymentProcessorAccountIdMappingValue
    if (currencyMapping) {
      const connectedAccountId = this.connectedStripeAccountId
      if (connectedAccountId) {
        for (const [currency, accountId] of Object.entries(currencyMapping)) {
          if (accountId === connectedAccountId) return currency
        }
      }
    }

    return null
  }

  showPaymentMethodSelectionInterface() {
    const connectedDisplay = this.element.querySelector('[data-payment-connection-target="connectedPaymentMethod"]')
    if (connectedDisplay) connectedDisplay.style.display = 'none'

    const paymentOptions = this.element.querySelector('[data-payment-connection-target="paymentOptions"]')
    if (paymentOptions) paymentOptions.style.display = 'block'
  }

  setPaymentMethodData(buttonType, paymentMethodId, stripePaymentMethodType = null) {
    const actualStripeType = stripePaymentMethodType || buttonType

    if (this.hasPaymentMethodHiddenInputTarget) {
      this.paymentMethodHiddenInputTarget.value = buttonType
    } else {
      const fallbackField = document.querySelector(this.fieldSelector('payment_method_id'))
      if (fallbackField) fallbackField.value = buttonType
    }

    const paymentMethodIdField = document.querySelector(this.fieldSelector('payment_method_id'))
    if (paymentMethodIdField) paymentMethodIdField.value = buttonType

    const stripePaymentMethodIdField = document.querySelector(this.fieldSelector('payment_processor_payment_method_id'))
    if (stripePaymentMethodIdField) stripePaymentMethodIdField.value = paymentMethodId

    if (this.hasPaymentMethodTypeInputTarget) this.paymentMethodTypeInputTarget.value = actualStripeType
    if (this.hasPaymentMethodCustomerIdInputTarget) this.paymentMethodCustomerIdInputTarget.value = ''
  }

  ensurePaymentMethodDataIsSet() {
    const paymentMethodType = this.paymentMethodTypeInputTarget?.value
    const paymentMethodId = document.querySelector(this.fieldSelector('payment_processor_payment_method_id'))?.value

    if (paymentMethodType && paymentMethodId) {
      this.setPaymentMethodData(paymentMethodType, paymentMethodId)
    }
  }

  validatePaymentMethod() {
    const paymentMethodType = this.paymentMethodTypeInputTarget?.value
    const paymentMethodId = document.querySelector(this.fieldSelector('payment_processor_payment_method_id'))?.value
    const hasConnectedPaymentMethod = !!(paymentMethodType && paymentMethodId)

    const errorElement = this.element.querySelector('[data-payment-connection-target="paymentOptions"] .error-badge')

    if (!hasConnectedPaymentMethod) {
      if (errorElement) errorElement.style.display = 'block'
      return false
    } else {
      if (errorElement) errorElement.style.display = 'none'
      return true
    }
  }

  get connectedStripeAccountId() {
    const selectedPortfolioId = this.element.querySelector(this.fieldSelector('managed_portfolio_id'))?.value
    if (selectedPortfolioId) {
      const portfolioElement = this.element.querySelector(`[data-radio-select-value="${selectedPortfolioId}"]`)
      if (portfolioElement && portfolioElement.dataset.stripeAccountId) {
        return portfolioElement.dataset.stripeAccountId
      }
    }

    // Fallback to currency-based mapping for backward compatibility — needs the wizard's
    // current currency, since that state lives there, not here.
    const wizard = this.getWizardController()
    return this.currencyToPaymentProcessorAccountIdMappingValue[wizard?.currencyCodeValue]
  }
}
